// CTV-610: client helpers for the landing background-video builder. Upload
// mirrors the logo/creative signed-URL pattern (POST for a signed URL, then
// PUT the file straight to Supabase Storage). Build kicks the synchronous
// render on the API and returns the final public URL.

import { apiClient } from '../../../lib/apiClient';
import { ADMIN_UPLOAD_ENDPOINTS, type UploadEndpoints } from './uploadEndpoints';

export const MAX_BACKGROUND_CLIPS = 6;
export const MAX_CLIP_BYTES = 150 * 1024 * 1024; // matches the bucket cap
export const ACCEPTED_CLIP_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
// The render is always forced to exactly this many seconds (TARGET_DURATION_SEC
// in the API's background-video.util.ts, which also equals the landing text
// cycle). Surfaced here so the editor can show progress toward filling it.
export const TARGET_DURATION_SEC = 24;

interface ClipUploadUrlResponse {
  uploadUrl: string;
  storagePath: string;
  token: string;
}

/** Upload one raw clip; resolves the storagePath the build call references. */
export async function uploadBackgroundClip(
  file: File,
  endpoints: UploadEndpoints = ADMIN_UPLOAD_ENDPOINTS,
): Promise<string> {
  const signed = await apiClient.post<ClipUploadUrlResponse>(
    endpoints.clipUploadUrlPath,
    { filename: file.name, mimeType: file.type, sizeBytes: file.size },
    endpoints.anonymous ? { anonymous: true } : undefined,
  );
  const put = await fetch(signed.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!put.ok) throw new Error(`Storage upload failed (${put.status})`);
  return signed.storagePath;
}

export interface BuildClipSpec {
  storagePath: string;
  inSec?: number;
  outSec?: number;
}

// CTV-745: a clip the publisher uploaded through the onboarding intake form,
// with a short-lived signed preview URL (the clips bucket is private).
export interface IntakeFootageClip {
  storagePath: string;
  url: string;
}

/**
 * The raw footage the publisher submitted with their onboarding form, so the
 * channel settings page can offer it for a re-cut of the landing video.
 * Empty when the station wasn't created from an intake submission.
 */
export async function fetchIntakeFootage(slug: string): Promise<IntakeFootageClip[]> {
  const res = await apiClient.get<{ clips?: IntakeFootageClip[] }>(
    `/stations/${encodeURIComponent(slug)}/intake-clips`,
  );
  return res?.clips ?? [];
}

/** Render + attach the stitched background video; resolves its public URL. */
export async function buildStationBackgroundVideo(
  slug: string,
  clips: BuildClipSpec[],
): Promise<string> {
  const res = await apiClient.post<{ backgroundVideoUrl: string }>(
    `/stations/${encodeURIComponent(slug)}/background-video/build`,
    { clips },
  );
  return res.backgroundVideoUrl;
}
